import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const Hero = () => {
  const { t } = useLanguage()
  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="pt-32 pb-12 bg-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center"
        >
            {/* Launch Announcement Badge */}
            <div className="inline-block mb-6 px-6 py-2 rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gradient-to-r from-blue-400 to-purple-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-gradient-to-r from-blue-500 to-purple-500"></span>
                </span>
                <span className="text-sm font-semibold text-gradient">{t('hero.launchingSoon')}</span>
              </div>
            </div>

            {/* Feature Badges */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
              <div className="px-4 py-1.5 rounded-full bg-green-600/20 border border-green-500/30">
                <span className="text-xs font-semibold text-green-400">{t('hero.badge.free')}</span>
              </div>
              <div className="px-4 py-1.5 rounded-full bg-blue-600/20 border border-blue-500/30">
                <span className="text-xs font-semibold text-blue-400">{t('hero.badge.banks')}</span>
              </div>
              <div className="px-4 py-1.5 rounded-full bg-purple-600/20 border border-purple-500/30">
                <span className="text-xs font-semibold text-purple-400">{t('hero.badge.ai')}</span>
              </div>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 text-white">
              <span className="text-white">{t('hero.title.line1Start')}</span><span className="font-extrabold text-gradient">{t('hero.title.line1AI')}</span><span className="text-white">{t('hero.title.line1End')}</span>
              <br />
              {t('hero.title.line2')}
              <br />
              <span className="text-gradient"><span className="text-white">{t('hero.title.line3')}</span><span className="font-extrabold text-gradient">{t('hero.title.line3AI')}</span><span className="text-white">{t('hero.title.line3End')}</span></span>
            </h1>

            <p className="text-xl text-gray-400 mb-8 leading-relaxed">
              {t('hero.description')}
            </p>

            {/* Bank Logos - Continuous Scroll */}
            <div className="mb-8">
              <div className="flex items-center justify-center gap-6 mb-6">
                <p className="text-sm text-gray-500 whitespace-nowrap">{t('hero.banksSupport')}</p>
                <div className="relative overflow-hidden flex-1 max-w-3xl">
                  {/* Gradient overlays for smooth fade effect */}
                  <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none"></div>
                  <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none"></div>
                
                <motion.div
                  animate={{ x: [0, -1296] }}
                  transition={{ 
                    duration: 30, 
                    repeat: Infinity, 
                    ease: "linear",
                    repeatType: "loop"
                  }}
                  className="flex items-center gap-12"
                >
                  {/* First set of logos */}
                  {/* Sparkasse - Red */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-red-600 to-red-800 border border-red-500 opacity-80 hover:opacity-100 hover:border-red-400 hover:shadow-lg hover:shadow-red-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">SK</span>
                  </div>
                  
                  {/* Deutsche Bank - Blue */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 border border-blue-500 opacity-80 hover:opacity-100 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">DB</span>
                  </div>
                  
                  {/* Commerzbank - Yellow */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 border border-yellow-400 opacity-80 hover:opacity-100 hover:border-yellow-300 hover:shadow-lg hover:shadow-yellow-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-gray-900">CB</span>
                  </div>
                  
                  {/* ING - Orange */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-orange-500 to-orange-700 border border-orange-400 opacity-80 hover:opacity-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">ING</span>
                  </div>
                  
                  {/* DKB - Deep Blue */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-blue-700 to-blue-900 border border-blue-600 opacity-80 hover:opacity-100 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-600/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">DKB</span>
                  </div>
                  
                  {/* N26 - Cyan/Turquoise */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-600 border border-cyan-400 opacity-80 hover:opacity-100 hover:border-cyan-300 hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">N26</span>
                  </div>
                  
                  {/* Postbank - Gold/Yellow */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-amber-500 to-yellow-600 border border-amber-400 opacity-80 hover:opacity-100 hover:border-amber-300 hover:shadow-lg hover:shadow-amber-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-gray-900">PB</span>
                  </div>

                  {/* Comdirect - Green */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-green-600 to-green-800 border border-green-500 opacity-80 hover:opacity-100 hover:border-green-400 hover:shadow-lg hover:shadow-green-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">CD</span>
                  </div>

                  {/* Santander - Red */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-red-600 to-red-800 border border-red-500 opacity-80 hover:opacity-100 hover:border-red-400 hover:shadow-lg hover:shadow-red-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">SAN</span>
                  </div>

                  {/* Duplicate set for seamless loop */}
                  {/* Sparkasse - Red */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-red-600 to-red-800 border border-red-500 opacity-80 hover:opacity-100 hover:border-red-400 hover:shadow-lg hover:shadow-red-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">SK</span>
                  </div>
                  
                  {/* Deutsche Bank - Blue */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 border border-blue-500 opacity-80 hover:opacity-100 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">DB</span>
                  </div>
                  
                  {/* Commerzbank - Yellow */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 border border-yellow-400 opacity-80 hover:opacity-100 hover:border-yellow-300 hover:shadow-lg hover:shadow-yellow-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-gray-900">CB</span>
                  </div>
                  
                  {/* ING - Orange */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-orange-500 to-orange-700 border border-orange-400 opacity-80 hover:opacity-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">ING</span>
                  </div>
                  
                  {/* DKB - Deep Blue */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-blue-700 to-blue-900 border border-blue-600 opacity-80 hover:opacity-100 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-600/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">DKB</span>
                  </div>
                  
                  {/* N26 - Cyan/Turquoise */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-600 border border-cyan-400 opacity-80 hover:opacity-100 hover:border-cyan-300 hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">N26</span>
                  </div>
                  
                  {/* Postbank - Gold/Yellow */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-amber-500 to-yellow-600 border border-amber-400 opacity-80 hover:opacity-100 hover:border-amber-300 hover:shadow-lg hover:shadow-amber-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-gray-900">PB</span>
                  </div>

                  {/* Comdirect - Green */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-green-600 to-green-800 border border-green-500 opacity-80 hover:opacity-100 hover:border-green-400 hover:shadow-lg hover:shadow-green-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">CD</span>
                  </div>

                  {/* Santander - Red */}
                  <div className="flex-shrink-0 px-6 py-3 rounded-lg bg-gradient-to-br from-red-600 to-red-800 border border-red-500 opacity-80 hover:opacity-100 hover:border-red-400 hover:shadow-lg hover:shadow-red-500/50 transition-all duration-300">
                    <span className="text-lg font-bold text-white">SAN</span>
                  </div>
                </motion.div>
              </div>
              </div>
            </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-12 justify-center">
            <button
              onClick={scrollToWaitlist}
              className="bg-white text-black px-8 py-4 rounded-lg font-semibold text-base hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              {t('hero.joinWaitlist')}
              <ArrowRight size={18} />
            </button>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-6 pt-8 border-t border-gray-800 max-w-2xl mx-auto">
            <div className="text-sm text-gray-400">
              <span className="font-bold text-white">9+</span> {t('hero.stats.banks')}
            </div>
            <div className="text-gray-700">•</div>
            <div className="text-sm text-gray-400">
              <span className="font-bold text-white">{t('hero.stats.realtime')}</span> {t('hero.stats.updates')}
            </div>
            <div className="text-gray-700">•</div>
            <div className="text-sm text-gray-400">
              <span className="font-bold text-white">{t('hero.stats.bankLevel')}</span> {t('hero.stats.security')}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Hero
